import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit, doc, deleteDoc, updateDoc, Timestamp, where } from 'firebase/firestore';
import { db } from '../../firebase';
import {
    Search,
    RefreshCw,
    Trash2,
    Edit,
    Filter,
    DollarSign,
    X,
    Save
} from 'lucide-react';

interface Conversion {
    id: string;
    click_id: string;
    affiliate_id: string;
    product_id: string;
    amount: number;
    currency: string;
    commission_generated: number;
    conversion_id?: string;
    event_type: string;
    status: 'pending' | 'approved' | 'rejected' | 'paid';
    timestamp: any;
    sub_id?: string;
    source_id?: string;
    country?: string;
}

export default function AdminConversions() {
    const [conversions, setConversions] = useState<Conversion[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [editingConversion, setEditingConversion] = useState<Conversion | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Edit form state
    const [editData, setEditData] = useState({
        amount: 0,
        commission_generated: 0,
        status: '' as any,
        event_type: ''
    });

    useEffect(() => {
        fetchConversions();
    }, [statusFilter]);

    const fetchConversions = async () => {
        setLoading(true);
        try {
            let convQuery = query(collection(db, 'conversions'), orderBy('timestamp', 'desc'), limit(100));

            if (statusFilter !== 'all') {
                convQuery = query(
                    collection(db, 'conversions'),
                    where('status', '==', statusFilter),
                    orderBy('timestamp', 'desc'),
                    limit(100)
                );
            }

            const snapshot = await getDocs(convQuery);
            setConversions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Conversion[]);
        } catch (error) {
            console.error('Error fetching conversions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Are you sure you want to delete this conversion? This will remove it from the system and affiliate stats.')) return;

        setActionLoading(true);
        try {
            await deleteDoc(doc(db, 'conversions', id));
            setConversions(conversions.filter(c => c.id !== id));
        } catch (error) {
            console.error('Error deleting conversion:', error);
            alert('Failed to delete conversion');
        } finally {
            setActionLoading(false);
        }
    };

    const openEditModal = (conv: Conversion) => {
        setEditingConversion(conv);
        setEditData({
            amount: conv.amount,
            commission_generated: conv.commission_generated,
            status: conv.status,
            event_type: conv.event_type
        });
        setShowEditModal(true);
    };

    const handleUpdate = async () => {
        if (!editingConversion) return;

        setActionLoading(true);
        try {
            await updateDoc(doc(db, 'conversions', editingConversion.id), {
                ...editData,
                updated_at: Timestamp.now()
            });
            setShowEditModal(false);
            fetchConversions();
        } catch (error) {
            console.error('Error updating conversion:', error);
            alert('Failed to update conversion');
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (ts: any) => {
        if (!ts) return '-';
        const date = ts.toDate ? ts.toDate() : new Date(ts);
        return date.toLocaleString();
    };

    const filteredConversions = conversions.filter(c => {
        if (!searchTerm) return true;
        const s = searchTerm.toLowerCase();
        return (
            c.id.toLowerCase().includes(s) ||
            c.click_id.toLowerCase().includes(s) ||
            c.affiliate_id.toLowerCase().includes(s) ||
            c.product_id.toLowerCase().includes(s) ||
            (c.sub_id && c.sub_id.toLowerCase().includes(s))
        );
    });

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'approved': return 'text-emerald-400 bg-emerald-500/10';
            case 'pending': return 'text-amber-400 bg-amber-500/10';
            case 'rejected': return 'text-red-400 bg-red-500/10';
            case 'paid': return 'text-blue-400 bg-blue-500/10';
            default: return 'text-gray-400 bg-gray-500/10';
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-white">Conversions</h1>
                    <p className="text-gray-400">Manage affiliate conversions and payouts</p>
                </div>
                <button onClick={fetchConversions} disabled={loading} className="btn-secondary flex items-center gap-2">
                    <RefreshCw size={20} className={loading ? 'animate-spin' : ''} /> Refresh
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type="text"
                        placeholder="Search by ID, Click ID, Affiliate ID, Product ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="input-field pl-12"
                    />
                </div>
                <div className="relative">
                    <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="input-field pl-12"
                    >
                        <option value="all">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="paid">Paid</option>
                    </select>
                </div>
            </div>

            <div className="card overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="border-b border-white/5 text-gray-400 text-sm">
                            <th className="py-4 px-4 font-medium">Date</th>
                            <th className="py-4 px-4 font-medium">Conversion ID</th>
                            <th className="py-4 px-4 font-medium">Affiliate</th>
                            <th className="py-4 px-4 font-medium">Product</th>
                            <th className="py-4 px-4 font-medium">Amount</th>
                            <th className="py-4 px-4 font-medium">Payout</th>
                            <th className="py-4 px-4 font-medium">Status</th>
                            <th className="py-4 px-4 font-medium text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {loading ? (
                            <tr>
                                <td colSpan={8} className="py-12 text-center">
                                    <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                                </td>
                            </tr>
                        ) : filteredConversions.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="py-12 text-center text-gray-500">
                                    No conversions found
                                </td>
                            </tr>
                        ) : (
                            filteredConversions.map((conv) => (
                                <tr key={conv.id} className="hover:bg-white/5 transition-colors">
                                    <td className="py-4 px-4 text-xs text-gray-400">
                                        {formatDate(conv.timestamp)}
                                    </td>
                                    <td className="py-4 px-4 text-sm font-mono text-gray-300">
                                        <span className="select-all" title={conv.id}>
                                            {conv.id.substring(0, 8)}...
                                        </span>
                                    </td>
                                    <td className="py-4 px-4 text-sm text-gray-300">
                                        <div className="flex flex-col">
                                            <span>{conv.affiliate_id.substring(0, 8)}...</span>
                                            <span className="text-[10px] text-gray-500">{conv.sub_id || '-'}</span>
                                        </div>
                                    </td>
                                    <td className="py-4 px-4 text-sm text-gray-300">
                                        {conv.product_id.substring(0, 8)}...
                                    </td>
                                    <td className="py-4 px-4 text-sm font-semibold text-white">
                                        {conv.amount} {conv.currency}
                                    </td>
                                    <td className="py-4 px-4 text-sm font-semibold text-emerald-400">
                                        {conv.commission_generated.toFixed(2)} {conv.currency}
                                    </td>
                                    <td className="py-4 px-4">
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(conv.status)}`}>
                                            {conv.status}
                                        </span>
                                    </td>
                                    <td className="py-4 px-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => openEditModal(conv)}
                                                className="p-1 hover:text-primary-400 transition-colors"
                                                title="Edit"
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(conv.id)}
                                                className="p-1 hover:text-red-400 transition-colors"
                                                title="Delete"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Edit Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="card w-full max-w-md animate-scale-in">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-white">Edit Conversion</h2>
                            <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-white">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2 font-inter">Amount</label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                                    <input
                                        type="number"
                                        value={editData.amount}
                                        onChange={(e) => setEditData({ ...editData, amount: parseFloat(e.target.value) })}
                                        className="input-field pl-10"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2 font-inter">Payout (Commission)</label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
                                    <input
                                        type="number"
                                        value={editData.commission_generated}
                                        onChange={(e) => setEditData({ ...editData, commission_generated: parseFloat(e.target.value) })}
                                        className="input-field pl-10 text-emerald-400"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2 font-inter">Status</label>
                                <select
                                    value={editData.status}
                                    onChange={(e) => setEditData({ ...editData, status: e.target.value as any })}
                                    className="input-field"
                                >
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="paid">Paid</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2 font-inter">Event Type</label>
                                <input
                                    type="text"
                                    value={editData.event_type}
                                    onChange={(e) => setEditData({ ...editData, event_type: e.target.value })}
                                    className="input-field"
                                />
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button onClick={() => setShowEditModal(false)} className="btn-secondary flex-1">Cancel</button>
                                <button
                                    onClick={handleUpdate}
                                    disabled={actionLoading}
                                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                                >
                                    {actionLoading ? <RefreshCw className="animate-spin" size={18} /> : <Save size={18} />}
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
